import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import { GameDifficulty } from 'graphql/types';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { Box, Flex } from 'components';
import CheckIcon from 'components/CheckIcon';

import { AppTheme } from 'theme';

import colors from 'lib/colors';
import {
  ScenesProgressContext,
  SceneWithProgress,
  TrainingCompletionRequirements,
} from 'pages/Lobby/ScenesProgressProvider';

import { SceneStatusEnum } from 'graphql/types';

import GameStatusIcon from '../../GameStatusIcon';

type SceneActions = {
  readonly?: boolean;
  selectedSceneId?: string;
  handleClick?: (sceneId: string | undefined) => void;
};

interface Props {
  scene: SceneWithProgress;
  gameDifficulty: GameDifficulty;
  showEmptyProgress?: boolean;
}

const Scene = ({
  scene,
  handleClick,
  selectedSceneId,
  readonly = false,
  gameDifficulty,
  showEmptyProgress = false,
}: Props & SceneActions) => {
  const { t } = useTranslation();
  const { trainingRequirements } = useContext(ScenesProgressContext);
  const onClick = () => {
    handleClick && handleClick(scene.id);
  };

  const { checkMarks, additionalNumber, sceneCompleted } = getCheckMarks(
    scene,
    gameDifficulty,
    trainingRequirements,
  );

  return (
    <Container
      onClick={onClick}
      selected={selectedSceneId === scene.id}
      readonly={readonly}
    >
      <SceneContent
        image={scene.image}
        selected={selectedSceneId === scene.id}
        sceneCompleted={sceneCompleted}
      >
        <SceneProgress>
          <span style={{ width: '100%' }}>{scene.name}</span>
          {!showEmptyProgress &&
            (sceneCompleted ? (
              <SceneCompletedBadge>
                {t('matchView.scene.completed', 'Dokončeno')}
              </SceneCompletedBadge>
            ) : (
              checkMarks && (
                <SceneCheckMarksContainer>
                  <>
                    {checkMarks.map(
                      ({ variant, marginLeft, stackNumber }, i) => (
                        <Box css={{ position: 'relative' }} key={i}>
                          <SceneCheckIcon
                            marginLeft={marginLeft}
                            backgroundColor={
                              variant === 'fulfilled' ? 'white' : '#B5B5B5'
                            }
                            foregroundColor={
                              variant === 'fulfilled'
                                ? colors.success
                                : colors.gray
                            }
                          />
                          {stackNumber && (
                            <CheckIconBadge>{stackNumber}</CheckIconBadge>
                          )}
                        </Box>
                      ),
                    )}
                    {additionalNumber && (
                      <SceneAdditionalNumber>{`+ ${additionalNumber}`}</SceneAdditionalNumber>
                    )}
                  </>
                </SceneCheckMarksContainer>
              )
            ))}
        </SceneProgress>
      </SceneContent>
    </Container>
  );
};

export default Scene;

export const SceneExtended = ({
  scene,
  handleClick,
  readonly = false,
  gameDifficulty,
  showEmptyProgress = false,
}: Props & SceneActions) => {
  const { t } = useTranslation();
  const { trainingRequirements } = useContext(ScenesProgressContext);
  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  const onClick = () => {
    handleClick && handleClick(scene.id);
  };

  const { checkMarks, additionalNumber, sceneCompleted } = getCheckMarks(
    scene,
    gameDifficulty,
    trainingRequirements,
  );

  const renderIcons = () => {
    const iconCss = { width: 50 };

    const isTournament =
      (scene.statuses || []).indexOf(SceneStatusEnum.TOURNAMENT) > -1;
    const isTraining =
      (scene.statuses || []).indexOf(SceneStatusEnum.TRAINING) > -1;
    const isUnfinished =
      (scene.statuses || []).indexOf(SceneStatusEnum.UNFINISHED) > -1;

    return (
      <SceneIcons>
        <div>
          {isTraining && (
            <GameStatusIcon css={iconCss} iconKey={SceneStatusEnum.TRAINING} />
          )}
        </div>
        <div>
          {isTournament && (
            <GameStatusIcon
              css={iconCss}
              iconKey={SceneStatusEnum.TOURNAMENT}
            />
          )}
        </div>
        <div>
          {isUnfinished && (
            <GameStatusIcon
              css={iconCss}
              iconKey={SceneStatusEnum.UNFINISHED}
            />
          )}
        </div>
      </SceneIcons>
    );
  };

  return (
    <ContainerButton
      onClick={onClick}
      selected={false}
      readonly={readonly}
      isEduLayout={IS_EDU}
      colors={theme?.colors}
    >
      <SceneContent
        image={scene.image}
        selected={false}
        sceneCompleted={sceneCompleted}
        isEduLayout={IS_EDU}
      >
        <SceneProgress css={{ marginBottom: 10 }}>
          {renderIcons()}

          <div>{scene.name}</div>

          {!showEmptyProgress &&
            (sceneCompleted ? (
              <SceneCompletedBadge>
                {t('matchView.scene.completed', 'Dokončeno')}
              </SceneCompletedBadge>
            ) : (
              checkMarks && (
                <SceneCheckMarksContainer css={{ marginTop: 10 }}>
                  {checkMarks.map(({ variant, stackNumber }, i) => (
                    <Box css={{ position: 'relative' }} key={i}>
                      <CheckCircleIcon
                        css={{
                          color:
                            variant === 'fulfilled'
                              ? colors.success
                              : colors.white,
                        }}
                      />
                      {stackNumber && (
                        <CheckIconBadge>{stackNumber}</CheckIconBadge>
                      )}
                    </Box>
                  ))}
                  {additionalNumber && (
                    <SceneAdditionalNumber>{`+ ${additionalNumber}`}</SceneAdditionalNumber>
                  )}
                </SceneCheckMarksContainer>
              )
            ))}
        </SceneProgress>
      </SceneContent>
    </ContainerButton>
  );
};

function getCheckMarks(
  scene: SceneWithProgress,
  gameDifficulty: GameDifficulty,
  trainingRequirements?: TrainingCompletionRequirements,
) {
  if (!scene.progress || !trainingRequirements) {
    return {
      checkMarks: null,
      additionalNumber: null,
      sceneCompleted: false,
    };
  }
  if (scene.progress.alreadyFinished) {
    return {
      checkMarks: null,
      additionalNumber: null,
      sceneCompleted: true,
    };
  }
  const { gameCount: currentProgressEasy } = scene.progress;

  const { finishedGamesInScene } = trainingRequirements;
  let privateStackNumber: number | undefined;
  let additionalNumber: number | undefined;
  let checkMarksCount = 5;
  if (finishedGamesInScene > 5) {
    if (currentProgressEasy > finishedGamesInScene / 2) {
      privateStackNumber = finishedGamesInScene - 2;
    } else {
      additionalNumber = finishedGamesInScene - 5;
    }
  } else {
    checkMarksCount = finishedGamesInScene;
  }

  const checkMarks = Array(checkMarksCount)
    .fill(0)
    .map((_, i) => {
      let stackNumber = undefined;
      let marginLeft = i === 0 ? 0 : -4;
      let variant = 'notFulfilled';
      if (privateStackNumber) {
        if (i <= currentProgressEasy - privateStackNumber) {
          variant = 'fulfilled';
        }
        if (i === 0) {
          stackNumber = privateStackNumber;
          variant = 'fulfilled';
        } else if (i === 1) {
          marginLeft = 4;
        }
      } else {
        if (i < currentProgressEasy) {
          variant = 'fulfilled';
        }
      }
      return {
        variant,
        stackNumber,
        marginLeft,
      };
    });
  return {
    checkMarks,
    additionalNumber,
    sceneCompleted: false,
  };
}

const SceneAdditionalNumber = styled('span')({
  fontSize: 12,
  margin: '0px 4px',
});
const SceneCheckMarksContainer = styled(Flex)({
  zIndex: 1,
  marginTop: 8,
  justifyContent: 'center',
  fontWeight: 'normal',
});

const SceneCompletedBadge = styled('span')({
  zIndex: 1,
  marginTop: 8,
  backgroundColor: colors.success,
  fontSize: 12,
  padding: '0px 10px',
  borderRadius: '6px',
});

interface ContainerProps {
  selected: boolean;
  readonly: boolean;
}

const Container = styled.div<ContainerProps>`
  pointer-events: ${({ readonly }) => (readonly ? 'none' : 'all')};
  user-select: none;
  z-index: 1;
  position: relative;
  margin: 8px;
  margin-bottom: 0;
  cursor: pointer;
  transform: ${({ selected }) =>
    selected ? 'translateY(-3px) scale(1.03)' : 'translateY(0px) scale(1)'};

  &:hover {
    transform: translateY(-3px) scale(1.03);

    &::after {
      opacity: 0.5;
    }
  }

  transition: scale 0.3s ease-in;
  transition: translateY 0.3s ease-in;
`;

interface ContainerButtonProps {
  selected: boolean;
  readonly: boolean;
  isEduLayout: boolean;
  colors: AppTheme['colors'];
}

const ContainerButton = styled.button<ContainerButtonProps>`
  pointer-events: ${({ readonly }) => (readonly ? 'none' : 'all')};
  user-select: none;
  z-index: 1;
  position: relative;
  margin: 8px;
  margin-bottom: 0;
  cursor: pointer;
  transform: ${({ selected }) =>
    selected ? 'translateY(-3px) scale(1.03)' : 'translateY(0px) scale(1)'};

  ${({ colors }) =>
    `border-radius: 4px;

    &:hover {
      outline: 3px solid ${colors?.lobbyOrange};
    }`}

  padding: 0;
  border: 0;
  color: ${({ colors }) => colors?.white};
  background: none;
`;

const coverPseudo = css`
  display: block;
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  border-radius: 5px;
`;

const outline = css`
  box-shadow: inset 0 0 0 4px ${colors.lobbyOrange};
`;

interface ContentProps {
  selected: boolean;
  image: string | null;
  sceneCompleted: boolean;
  isEduLayout?: boolean;
  isModalView?: boolean;
}

const hasImage = ({
  selected,
  image,
  sceneCompleted,
  isModalView,
}: ContentProps) =>
  css(
    image != null
      ? {
          '&::after': [
            coverPseudo,
            {
              background: `url("${image}") center/cover no-repeat`,
              opacity: isModalView
                ? 1
                : selected
                ? sceneCompleted
                  ? 0.65
                  : 0.5
                : sceneCompleted
                ? 0.45
                : 0.3,
              transition: 'opacity .3s ease-in',
            },
            selected && outline,
          ],
        }
      : {},
  );

export const SceneContent = styled.div<ContentProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  line-height: 18px;
  width: ${({ isEduLayout }) => (isEduLayout ? '200px' : '180px')};
  height: ${({ isEduLayout }) => (isEduLayout ? '290px' : '101px')};
  padding: 4px;

  &::before {
    ${coverPseudo};
    background-color: ${({ sceneCompleted }) =>
      sceneCompleted ? colors.successDark : colors.darkBlue};
    ${({ selected }) => selected && outline};
  }

  ${hasImage};
`;

const SceneIcons = styled('div')({
  marginBottom: 10,
  display: 'flex',
});

const SceneCheckIcon = styled(CheckIcon)<{ marginLeft: number }>(
  ({ marginLeft }) => ({
    marginLeft,
  }),
);
const CheckIconBadge = styled('span')({
  backgroundColor: colors.successDark,
  width: 12,
  height: 12,
  borderRadius: '50%',
  position: 'absolute',
  fontSize: 8,
  lineHeight: '12px',
  top: '-6px',
  right: '-5px',
});

const SceneProgress = styled(Flex)({
  zIndex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});
